import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../app.css";
import BlogBody from "./BlogBody";
// import RecomendedTopics from './blogsidebar/RecomendedTopics';
import { Link, useParams } from "react-router-dom";
import { constant } from "../../utils/constant";
import { Skeleton } from "@mui/material";
import { formatDate } from "../../helper/DateFormater";
import useCanonical from "../../helper/useCanonical";
import { formatTitleForURL } from "../../helper/FormatUrl";
import { countThatFit } from "../../helper/fitCount";

// Upper bound on the related-posts sidebar; the article's height decides how
// many of these actually render.
const MAX_SIDEBAR_POSTS = 5;

const Blog = ({ allCategory }) => {
  const [data, setData] = useState(null);
  const [recentBlog, setRecentBlog] = useState([]);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();
  const categories = useMemo(
    () => ["Entertainment", "News", "Marketing", "Sports"],
    []
  );
  const [currentCategory, setCurrentCategory] = useState([]);
  const [visibleSidebarCount, setVisibleSidebarCount] = useState(null);
  // Bumped on resize purely to force the measuring effect to run again.
  const [measureToken, setMeasureToken] = useState(0);
  const articleRef = useRef(null);
  const sidebarRef = useRef(null);
  const cardHeightsRef = useRef([]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${constant.CONST_SARCASM_BLOG}/${slug}`);
      const json = await response.json();
      // A miss comes back as `success: false` with HTTP 200. Assign either way,
      // otherwise moving to a blog that no longer exists kept the previous one
      // on screen.
      setData(json.success ? json.data : null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentBlog = async () => {
    try {
      const response = await fetch(constant.CONST_SARCASM_BLOG_RECENT);
      const json = await response.json();
      if (json.success) setRecentBlog(json.data);
    } catch (error) {
      console.error("Error fetching recent blogs:", error);
    }
  };

  useEffect(() => {
    // Back to the skeleton on every slug change — without this the previous
    // post stayed rendered while the new one was still loading.
    setLoading(true);
    setData(null);
    fetchData();
    fetchRecentBlog();
    window.scrollTo(0, 0);
  }, [slug]);

  const getCategoryName = (categoryId) => {
    const category = allCategory.find((cat) => cat._id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  useEffect(() => {
    if (recentBlog.length > 0 && data) {
      const filteredBlogs = recentBlog.reduce((acc, item) => {
        if (categories.includes(item.categoryName)) {
          acc[item.categoryName] = acc[item.categoryName] || [];
          acc[item.categoryName].push(item);
        }
        return acc;
      }, {});

      const categoryName = getCategoryName(data.blogCategoryId);
      setCurrentCategory(filteredBlogs[categoryName] || []);
    }
  }, [recentBlog, data, categories, allCategory]);

  // ---- Related posts sidebar -------------------------------------------------
  // It used to render a fixed five cards, so a short article left a long empty
  // gap beside them. Now only as many cards as fit alongside the article are
  // kept. `null` means "not measured yet" — render them all so their heights can
  // be read, then trim.
  const sidebarCandidates = useMemo(
    () =>
      (currentCategory[0]?.recentBlogs || [])
        // Filter first, then take five: the old code sliced (1, 6) before
        // filtering, which threw away a usable post whenever the one it skipped
        // was not the post being read.
        .filter(
          (item) =>
            item?.bannerImageUrl !== null && item?.slug && item.slug !== slug
        )
        .slice(0, MAX_SIDEBAR_POSTS),
    [currentCategory, slug]
  );

  useLayoutEffect(() => {
    const article = articleRef.current;
    const sidebar = sidebarRef.current;
    if (!article || !sidebar || sidebarCandidates.length === 0) return undefined;

    const measure = () => {
      // Below `lg` the sidebar stacks underneath the article instead of beside
      // it, so there is no gap to close and nothing should be hidden.
      if (!window.matchMedia("(min-width: 992px)").matches) {
        setVisibleSidebarCount(sidebarCandidates.length);
        return;
      }
      // Only re-read heights while every candidate is on screen; once trimmed,
      // reuse what was captured then.
      const cards = Array.from(sidebar.children);
      if (cards.length === sidebarCandidates.length) {
        cardHeightsRef.current = cards.map((card) => card.offsetHeight);
      }
      setVisibleSidebarCount(
        countThatFit(cardHeightsRef.current, article.offsetHeight)
      );
    };

    measure();

    // The article grows as its images and embeds load, so one measurement up
    // front is not enough.
    const observer = new ResizeObserver(measure);
    observer.observe(article);

    // A viewport change rewraps the card titles, so the stored heights are
    // stale. Show everything again *and* bump the token: both land in one
    // render, which re-runs this effect with the full set on screen, so the
    // fresh measurement is taken from all the cards rather than the trimmed
    // list. (Measuring straight from the resize handler does not work — it can
    // run before React has re-rendered, and then nothing triggers it again.)
    let debounce;
    const remeasureFromScratch = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        cardHeightsRef.current = [];
        setVisibleSidebarCount(null);
        setMeasureToken((token) => token + 1);
      }, 150);
    };
    window.addEventListener("resize", remeasureFromScratch);

    return () => {
      clearTimeout(debounce);
      observer.disconnect();
      window.removeEventListener("resize", remeasureFromScratch);
    };
  }, [sidebarCandidates, data, measureToken]);

  const sidebarPosts =
    visibleSidebarCount === null
      ? sidebarCandidates
      : sidebarCandidates.slice(0, visibleSidebarCount);

  const currentURL = window.location.href;
  const title = `Check out this blog post: ${data?.title || "Loading..."}`;

  // Replaces a <Helmet> block that injected nothing (see helper/useCanonical).
  // The canonical applies straight away from the slug; the title and description
  // fill in once the post has loaded. Must sit above the `loading` early return —
  // hooks cannot be called conditionally.
  useCanonical(
    `/blog/${slug}`,
    data?.metaTitle || data?.title,
    data?.metaDescription
  );

  if (loading) {
    return (
      <div style={{ marginTop: "1rem" }}>
        {[...Array(7)].map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width="65%"
            style={{ margin: "0 auto 20px" }}
            height={100}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <section className="space-top-60 space-bottom-60 single-blog-wrap1 bg-color-light-1 transition-default">
        <div className="container">
          <div className="row sticky-coloum-wrap">
            <div className="col-lg-8 sticky-coloum-item">
              {/* The ref goes on the content, not the column: `.row` is a flex
                  container, so both columns stretch to whichever is taller and
                  the column's own height says nothing about the article. */}
              <div
                className="single-blog-content content-layout1 pe-lg-4"
                ref={articleRef}
              >
                {/* <BlogPostLayout /> */}
                {/* <BlogArticleContent /> */}
                {/* <TagShareSection /> */}
                {/* <AuthorBio />
                <PostNavigation />
                <CommentSection /> */}
                {/* Social Media Share Buttons */}
                <div className="entry-category style-2 color-dark-1">
                  <ul>
                    <li>
                      <a href="archive-layout1.html">
                        {data?.blogCategoryName}
                      </a>
                    </li>
                  </ul>
                </div>
                <h1 className="entry-title color-dark-1">{data?.title}</h1>
                <ul className="entry-meta color-dark-1">
                  <li className="post-author">
                    Posted By :<a>{data?.posted_by} |</a>
                    Date: {formatDate(data?.updatedAt)}
                  </li>
                </ul>
                <div className="axil-social social-layout-1 size-large gap-12">
                  <ul>
                    <li className="twitter">
                      <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                          title
                        )}&url=${encodeURIComponent(currentURL)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Share on Twitter"
                      >
                        <i className="bi bi-twitter-x" />
                      </a>
                    </li>
                    <li className="linkedin">
                      <a
                        href={`https://www.linkedin.com/shareArticle?title=${encodeURIComponent(
                          title
                        )}&url=${encodeURIComponent(currentURL)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Share on LinkedIn"
                      >
                        <i className="bi bi-linkedin" />
                      </a>
                    </li>
                    <li className="whatsapp">
                      <a
                        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                          title + " " + currentURL
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Share on WhatsApp"
                      >
                        <i className="bi bi-whatsapp"></i>
                      </a>
                    </li>
                    <li className="facebook">
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          currentURL
                        )}&quote=${encodeURIComponent(title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Share on Facebook"
                      >
                        <i className="bi bi-facebook" />
                      </a>
                    </li>
                    <li className="instagram">
                      <a
                        href={`https://www.instagram.com/?url=${encodeURIComponent(
                          currentURL
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Share on Instagram"
                      >
                        <i className="bi bi-instagram"></i>
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="singleBlogContent">
                  <BlogBody data={data} />
                </div>
                {/* -------------------------------------------- */}
              </div>
            </div>
            <div className="col-lg-4 sticky-coloum-item" ref={sidebarRef}>
              {sidebarPosts.map((item) => (
                    <div
                      className="col-12"
                      key={item?._id}
                      style={{ padding: "1rem" }}
                    >
                      <div className="box-border-dark-1 radius-default padding-20 figure-scale-animation bg-color-scandal box-shadow-large shadow-style-2 transition-default">
                        <div className="figure-holder radius-default">
                          <Link
                            to={`/blog/${item?.slug}`}
                            className="link-wrap"
                          >
                            <img
                              className="object-fit-cover w-100"
                              height="280"
                              src={item?.bannerImageUrl}
                              alt="Post"
                            />
                          </Link>
                        </div>
                        <div className="content-holder d-flex justify-content-center mt-2">
                          <div>
                            <div className="entry-category style-2 color-dark-1-fixed ">
                              <ul>
                                <li>
                                  <Link
                                    to={`/blog/${item?.slug}`}
                                  >
                                    {getCategoryName(item?.blogCategoryId)}
                                  </Link>
                                </li>
                              </ul>
                            </div>
                            <h3 className="entry-title color-dark-1-fixed underline-animation">
                              <Link
                                to={`/blog/${item?.slug}`}
                                className="link-wrap"
                              >
                                {item?.title}
                              </Link>
                            </h3>
                            {/* <p className="entry-description color-dark-1-fixed">
                            Nam eget lorem mattis, consequat felis quis, luctus
                            augue. Aenean ac iaculis enim.
                          </p> */}
                            {/* <ul className="entry-meta color-dark-1-fixed">
                            <li className="post-author">
                              <a href="author.html">
                                <img src={profileImg1} alt="Author" />
                                Esther Howard
                              </a>
                            </li>
                            <li>
                              <i className="bi bi-clock" />3 min read
                            </li>
                            <li>
                              <i className="bi bi-eye" />
                              4k
                            </li>
                          </ul> */}
                          </div>
                        </div>
                      </div>
                    </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
